/**
 * Composable: useAdminDashboardConfig
 *
 * Fetches admin_dashboard_settings via GET /admin/config/settings/admin_dashboard_settings
 * and produces normalized, validated sections, periods, and navigation links.
 *
 * The admin config settings endpoint returns a full setting record:
 *   { key, name, description, type, category, value: { enabled, sections, ... } }
 */
import { computed, ref } from 'vue';
import { useHCommon } from './use-page-common';
import { widgetId } from '../utils/dashboard-query-utils.js';

/**
 * Normalizes API payloads to the effective dashboard config object.
 * Supports both direct config payloads and setting-record payloads with nested `value`.
 */
function unwrapDashboardConfig(payload) {
  let cfg = payload;

  // Common shape from settings endpoint: { key, ..., value: <config|string> }
  if (cfg && typeof cfg === 'object' && Object.prototype.hasOwnProperty.call(cfg, 'value')) {
    cfg = cfg.value;
  }

  // Some APIs store the full setting record as JSON string in `value`.
  if (typeof cfg === 'string') {
    cfg = JSON.parse(cfg);
  }

  // If the parsed object is still a setting record, unwrap once more.
  if (cfg && typeof cfg === 'object' && Object.prototype.hasOwnProperty.call(cfg, 'value')) {
    cfg = cfg.value;
  }

  return cfg ?? null;
}

/**
 * Assigns stable _id fields to all widgets (and breakdown items) in-place.
 */
function assignIds(sections) {
  for (const section of sections) {
    for (const widget of section.children || []) {
      widget._id = widgetId(section.name, widget.name);
      // Breakdown items also get IDs
      if (widget.items) {
        for (const item of widget.items) {
          item._id = widgetId(section.name, `${widget.name}_${item.name}`);
        }
      }
    }
  }
  return sections;
}

export function useAdminDashboardConfig() {
  const { hHttpRequest } = useHCommon();

  const loading = ref(true);
  const error = ref(null);
  const rawConfig = ref(null);

  const { data, execute } = hHttpRequest(
    { url: '/admin/config/settings/admin_dashboard_settings', method: 'get', immediate: false },
    { throwHttpErrors: false },
  );

  void execute()
    .then((response) => {
      if (!response) {
        error.value = 'Failed to load admin_dashboard_settings';
        return;
      }

      if (response.status === 404) {
        error.value = 'admin_dashboard_settings was not found in the API';
        return;
      }

      if (!response.ok) {
        error.value = `Failed to load admin_dashboard_settings (${response.status})`;
        return;
      }

      if (!data.value) {
        error.value = 'admin_dashboard_settings returned an empty response';
        return;
      }

      try {
        rawConfig.value = unwrapDashboardConfig(data.value);
      } catch (e) {
        error.value = e?.message ?? 'Failed to parse admin_dashboard_settings';
      }
    })
    .catch((e) => {
      error.value = e?.message ?? 'Failed to load admin_dashboard_settings';
    })
    .finally(() => {
      loading.value = false;
    });

  const config = computed(() => {
    try {
      return rawConfig.value;
    } catch {
      return null;
    }
  });

  const isEnabled = computed(() => config.value?.enabled !== false);

  const periods = computed(
    () =>
      config.value?.periods ?? [
        { label: 'Today', value: '1d', prevPeriod: '2d' },
        { label: '7d', value: '7d', prevPeriod: '14d' },
        { label: '30d', value: '30d', prevPeriod: '60d' },
        { label: '90d', value: '90d', prevPeriod: '180d' },
      ],
  );

  const defaultPeriod = computed(() => config.value?.defaultPeriod ?? '30d');

  const enabledSections = computed(() => {
    if (!isEnabled.value || !config.value?.sections) return [];
    const sections = config.value.sections
      .filter((s) => s.enabled !== false)
      .map((s) => ({
        ...s,
        children: (s.children || []).filter((w) => w.enabled !== false),
      }))
      .filter((s) => s.children.length > 0);
    return assignIds(sections);
  });

  const hasSections = computed(() => enabledSections.value.length > 0);

  const navigationLinks = computed(() => config.value?.navigation ?? []);

  return {
    loading,
    error,
    isEnabled,
    periods,
    defaultPeriod,
    enabledSections,
    hasSections,
    navigationLinks,
  };
}
